import React, { useState, useEffect, useRef } from 'react';
import { Bot, X, Send, Sparkles, User, RefreshCw, AlertCircle, ExternalLink, MessageCircle, Paperclip, Image, FileText } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useSubscription } from '../context/SubscriptionContext';
import { API_BASE_URL } from '../config/api';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTheme } from '../hooks/useTheme';

const customUrlTransform = (url: string) => {
  const lowercase = url.trim().toLowerCase();
  if (
    lowercase.startsWith('http://') ||
    lowercase.startsWith('https://') ||
    lowercase.startsWith('mailto:') ||
    lowercase.startsWith('tel:') ||
    lowercase.startsWith('tradingview://') ||
    lowercase.startsWith('tradingview:')
  ) {
    return url;
  }
  return '';
};

const markdownComponents = {
  h1: ({node, ...props}: any) => <h1 className="text-sm font-black text-white mt-4 mb-2 border-b border-neutral-800 pb-1" {...props} />,
  h2: ({node, ...props}: any) => <h2 className="text-xs font-black text-white mt-3 mb-2" {...props} />,
  h3: ({node, ...props}: any) => <h3 className="text-[11px] font-bold text-white mt-2 mb-1" {...props} />,
  p: ({node, ...props}: any) => <p className="mb-2 text-neutral-300 font-sans leading-relaxed" {...props} />,
  ul: ({node, ...props}: any) => <ul className="list-disc pl-4 mb-2 space-y-0.5 text-neutral-300" {...props} />,
  ol: ({node, ...props}: any) => <ol className="list-decimal pl-4 mb-2 space-y-0.5 text-neutral-300" {...props} />,
  li: ({node, ...props}: any) => <li className="font-sans" {...props} />,
  strong: ({node, ...props}: any) => <strong className="font-bold text-white" {...props} />,
  em: ({node, ...props}: any) => <em className="italic text-neutral-400" {...props} />,
  table: ({node, ...props}: any) => <div className="overflow-x-auto mb-2 border border-neutral-800 rounded-lg"><table className="min-w-full text-[10px] text-left text-neutral-300" {...props} /></div>,
  thead: ({node, ...props}: any) => <thead className="text-[9px] text-neutral-400 uppercase bg-neutral-900 border-b border-neutral-800" {...props} />,
  tbody: ({node, ...props}: any) => <tbody className="divide-y divide-neutral-800" {...props} />,
  tr: ({node, ...props}: any) => <tr className="hover:bg-white/5" {...props} />,
  th: ({node, ...props}: any) => <th className="px-2 py-1.5 font-medium text-white" {...props} />,
  td: ({node, ...props}: any) => <td className="px-2 py-1.5" {...props} />,
  code: ({node, inline, className, children, ...props}: any) => {
    return !inline ? (
      <div className="rounded-xl overflow-hidden mb-2 border border-neutral-800">
        <pre className="bg-neutral-950 p-2 overflow-x-auto text-[10px] text-neutral-300 font-mono">
          <code className={className} {...props}>{children}</code>
        </pre>
      </div>
    ) : (
      <code className="bg-neutral-800 text-red-400 px-1 rounded text-[10px] font-mono" {...props}>{children}</code>
    );
  },
  blockquote: ({node, ...props}: any) => <blockquote className="border-l-2 border-red-500 pl-3 italic my-2 text-neutral-400" {...props} />,
  a: ({node, href, children, ...props}: any) => {
    const isTv = href && (href.startsWith('tradingview://') || href.startsWith('tradingview:') || href.includes('tradingview.com'));
    if (isTv) {
      let symbol = 'BINANCE:BTCUSDT';
      if (href.startsWith('tradingview://')) {
        symbol = href.replace('tradingview://', '');
      } else if (href.startsWith('tradingview:')) {
        symbol = href.replace('tradingview:', '');
      } else if (href.includes('symbol=')) {
        const match = href.match(/[?&]symbol=([^&]+)/);
        if (match) symbol = decodeURIComponent(match[1]);
      } else {
        const parts = href.split('/');
        const lastPart = parts[parts.length - 1] || parts[parts.length - 2];
        if (lastPart && lastPart.toUpperCase() === lastPart) {
          symbol = `BINANCE:${lastPart}`;
        }
      }
      symbol = symbol.trim().replace(/\/$/, '').toUpperCase();
      const theme = (typeof document !== 'undefined' && document.documentElement.classList.contains('light')) ? 'light' : 'dark';
      return (
        <div className="w-full my-2">
          <div className="text-[9px] font-bold uppercase tracking-widest text-red-500 mb-1 px-1">
            📈 {children || 'TradingView Chart'} ({symbol})
          </div>
          <div className="w-full h-[280px] rounded-2xl overflow-hidden border border-neutral-800 shadow-lg bg-black relative">
            <iframe
              src={`https://s.tradingview.com/widgetembed/?symbol=${symbol}&theme=${theme}&interval=D&hidesidetoolbar=1&symboledit=0&saveimage=0&toolbarbg=111111&timezone=Asia/Jakarta&locale=id`}
              width="100%"
              height="100%"
              frameBorder="0"
              allowFullScreen
              title={`TradingView Chart - ${symbol}`}
            />
          </div>
        </div>
      );
    }
    return <a className="text-red-500 hover:underline" href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>;
  }
};
interface Message {
  id: number;
  role: string; // "user", "model", "system"
  content: string;
  created_at: string;
  attachment_url?: string;
  attachment_name?: string;
  attachment_type?: string;
}

const cleanContent = (text: string) => {
  if (!text) return '';
  const index = text.indexOf('\n\n[USER UPLOADED SPREADSHEET:');
  if (index !== -1) {
    return text.substring(0, index);
  }
  return text;
};

const DISCORD_URL = 'https://discord.gg/RDs4WqETc3';

// ============================================================
// NOTE: Human CS / live agent escalation via third-party
// platforms (Slack, Zendesk, Intercom, Freshdesk, etc.) is
// temporarily commented out.
//
// Current support channel: Discord server (discord.gg/catatcrypto)
// Users can open a ticket there and the team responds ASAP.
//
// Next step: integrate a proper helpdesk platform (e.g. Zendesk
// or Freshdesk) when the team scales.
// ============================================================

export const NeoAIWidget: React.FC = () => {
  const { effectivePlan } = useSubscription();
  const isDarkMode = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [thinkingStatus, setThinkingStatus] = useState('Nera AI sedang menghubungkan...');

  // File upload states
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [attachedUrl, setAttachedUrl] = useState('');
  const [attachedBase64, setAttachedBase64] = useState('');
  const [attachedCsvContent, setAttachedCsvContent] = useState('');
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClearAttachment = () => {
    setSelectedFile(null);
    setAttachedUrl('');
    setAttachedBase64('');
    setAttachedCsvContent('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const processFile = async (file: File) => {
    setSelectedFile(file);
    setIsUploadingFile(true);

    try {
      // 1. Read file as base64
      const base64Promise = new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = error => reject(error);
        reader.readAsDataURL(file);
      });
      const base64Data = await base64Promise;
      setAttachedBase64(base64Data);

      // 2. Parse Excel/CSV if spreadsheet
      const isSpreadsheet = file.name.endsWith('.xlsx') || file.name.endsWith('.xls') || file.name.endsWith('.csv') || file.type === 'text/csv' || file.type.includes('spreadsheet');
      if (isSpreadsheet) {
        if (file.name.endsWith('.csv')) {
          const textPromise = new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = error => reject(error);
            reader.readAsText(file);
          });
          const text = await textPromise;
          setAttachedCsvContent(text);
        } else {
          // XLSX / XLS
          const arrayBufferPromise = new Promise<ArrayBuffer>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as ArrayBuffer);
            reader.onerror = error => reject(error);
            reader.readAsArrayBuffer(file);
          });
          const buffer = await arrayBufferPromise;
          const XLSX = await import('xlsx');
          const data = new Uint8Array(buffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const csv = XLSX.utils.sheet_to_csv(worksheet);
          setAttachedCsvContent(csv);
        }
      }

      // 3. Upload file to backend /api/upload
      const formData = new FormData();
      formData.append('file', file);
      
      const uploadResp = await fetch(`${API_BASE_URL}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (uploadResp.ok) {
        const uploadData = await uploadResp.json();
        if (uploadData.success && uploadData.url) {
          setAttachedUrl(uploadData.url);
        }
      } else {
        console.error('Failed to upload file to R2');
      }
    } catch (error) {
      console.error('Error processing uploaded file:', error);
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await processFile(file);
    }
  };

  const handlePaste = async (e: React.ClipboardEvent<HTMLInputElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          await processFile(file);
          break; // only process first image
        }
      }
    }
  };
  
  // Draggable state
  const [position, setPosition] = useState(() => {
    const saved = localStorage.getItem('neoai_widget_position');
    return saved ? JSON.parse(saved) : { x: window.innerWidth - 100, y: window.innerHeight - 100 };
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  // Icon tilt state (desktop only)
  const [iconRotation, setIconRotation] = useState(0);
  const widgetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isLoading) {
      setThinkingStatus('Nera AI sedang menghubungkan...');
      return;
    }

    const start = Date.now();
    const interval = setInterval(() => {
      const elapsed = (Date.now() - start) / 1000;
      if (elapsed >= 8.5) {
        setThinkingStatus('Nera AI sedang merumuskan rekomendasi...');
      } else if (elapsed >= 6.0) {
        setThinkingStatus('Nera AI sedang mendeteksi pola psikologi trading...');
      } else if (elapsed >= 3.5) {
        setThinkingStatus('Nera AI sedang menghitung metrik Sharpe & Expectancy...');
      } else if (elapsed >= 1.5) {
        setThinkingStatus('Nera AI sedang menganalisis jurnal transaksi...');
      } else {
        setThinkingStatus('Nera AI sedang menghubungkan...');
      }
    }, 500);

    return () => clearInterval(interval);
  }, [isLoading]);

  // Draggable handlers
  const handleDragStart = (clientX: number, clientY: number) => {
    if (isOpen) return; // Don't drag when chat is open
    setIsDragging(true);
    setDragStart({ x: clientX - position.x, y: clientY - position.y });
  };

  const handleDragMove = (clientX: number, clientY: number) => {
    if (!isDragging) return;
    
    const newX = clientX - dragStart.x;
    const newY = clientY - dragStart.y;
    
    // Boundary detection
    const maxX = window.innerWidth - 56;
    const maxY = window.innerHeight - 56;
    
    const boundedX = Math.max(0, Math.min(newX, maxX));
    const boundedY = Math.max(0, Math.min(newY, maxY));
    
    setPosition({ x: boundedX, y: boundedY });
  };

  const handleDragEnd = () => {
    if (isDragging) {
      setIsDragging(false);
      localStorage.setItem('neoai_widget_position', JSON.stringify(position));
    }
  };

  // Mouse events for dragging
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => handleDragMove(e.clientX, e.clientY);
    const handleMouseUp = () => handleDragEnd();
    
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'grabbing';
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isDragging, dragStart, position]);

  // Touch events for mobile dragging
  useEffect(() => {
    const handleTouchMove = (e: TouchEvent) => {
      if (isDragging && e.touches[0]) {
        e.preventDefault();
        handleDragMove(e.touches[0].clientX, e.touches[0].clientY);
      }
    };
    
    const handleTouchEnd = () => handleDragEnd();
    
    if (isDragging) {
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
    }
    
    return () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, dragStart, position]);

  // Icon tilt following cursor (desktop only)
  useEffect(() => {
    if (isOpen || isDragging) return;
    
    const handleMouseMove = (e: MouseEvent) => {
      if (widgetRef.current && window.innerWidth >= 768) { // Desktop only
        const rect = widgetRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        
        const deltaX = e.clientX - centerX;
        const deltaY = e.clientY - centerY;
        
        // Calculate angle in degrees
        const angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);
        
        // Limit rotation to ±15 degrees for subtle effect
        const maxRotation = 15;
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        const maxDistance = 400; // Max distance for full rotation
        const intensity = Math.min(distance / maxDistance, 1);
        
        const limitedAngle = (angle / 180) * maxRotation * intensity;
        
        setIconRotation(limitedAngle);
      }
    };
    
    // Throttle mouse move for performance (60fps)
    let ticking = false;
    const throttledMouseMove = (e: MouseEvent) => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleMouseMove(e);
          ticking = false;
        });
        ticking = true;
      }
    };
    
    document.addEventListener('mousemove', throttledMouseMove);
    
    return () => {
      document.removeEventListener('mousemove', throttledMouseMove);
    };
  }, [isOpen, isDragging]);

  // Reset position on resize
  useEffect(() => {
    const handleResize = () => {
      const maxX = window.innerWidth - 56;
      const maxY = window.innerHeight - 56;
      
      setPosition(prev => ({
        x: Math.min(prev.x, maxX),
        y: Math.min(prev.y, maxY)
      }));
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // --- COMMENTED OUT: Human CS escalation states ---
  // const [isHumanActive, setIsHumanActive] = useState(false);
  // const [agentName, setAgentName] = useState('');
  // const [showEscalationSurvey, setShowEscalationSurvey] = useState(false);
  // const [selectedCategory, setSelectedCategory] = useState('');
  // const [surveyDetails, setSurveyDetails] = useState('');
  // const [surveyError, setSurveyError] = useState('');
  // const [isSubmittingSurvey, setIsSubmittingSurvey] = useState(false);
  // const [bypassHours, setBypassHours] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const token = localStorage.getItem('auth_token');

  const isLoggedIn = !!token;
  const isPublicPage = ['/', '/register', '/onboarding', '/login', '/ai-assistant'].includes(window.location.pathname);

  useEffect(() => {
    if (!isLoggedIn || isPublicPage) return;
    const timer = setTimeout(() => {
      setShowTooltip(true);
      setTimeout(() => setShowTooltip(false), 6000);
    }, 2500);
    return () => clearTimeout(timer);
  }, [isLoggedIn, isPublicPage]);

  useEffect(() => {
    if (isOpen && token) fetchHistory();
  }, [isOpen, token]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // --- COMMENTED OUT: Human CS polling interval ---
  // useEffect(() => {
  //   if (!isOpen || !token || !isHumanActive) return;
  //   const interval = setInterval(() => { fetchHistorySilent(); }, 3000);
  //   return () => clearInterval(interval);
  // }, [isOpen, token, isHumanActive]);

  const fetchHistory = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/neo/history`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data.data || []);
        // setIsHumanActive(data.is_human_active); // commented out: human CS
        // setAgentName(data.agent_name || '');      // commented out: human CS
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  };

  // --- COMMENTED OUT: Silent polling for human agent responses ---
  // const fetchHistorySilent = async () => { ... };

  const handleSendMessage = async (textToSend: string) => {
    const text = textToSend.trim();
    if (!text || isLoading || isStreaming || !token) return;

    const fileUrl = attachedUrl;
    const fileName = selectedFile?.name || '';
    const fileType = selectedFile?.type || '';
    const fileBase64 = attachedBase64;
    const fileCsvContent = attachedCsvContent;

    setInputMessage('');
    handleClearAttachment();
    setIsLoading(true);

    const tempUserMsg: Message = {
      id: Date.now(),
      role: 'user',
      content: text,
      attachment_url: fileUrl,
      attachment_name: fileName,
      attachment_type: fileType,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      const response = await fetch(`${API_BASE_URL}/api/neo/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/event-stream'
        },
        body: JSON.stringify({
          message: text,
          attachment_url: fileUrl,
          attachment_name: fileName,
          attachment_type: fileType,
          attachment_base64: fileBase64,
          attachment_csv_content: fileCsvContent
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMsg: Message = {
          id: Date.now() + 1,
          role: 'system',
          content: `Error: ${errorData.message || 'Failed to send message'}`,
          created_at: new Date().toISOString()
        };
        setMessages(prev => [...prev, errorMsg]);
        return;
      }

      // Check if this is a streaming response (Ollama) or regular JSON (Gemini)
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('text/event-stream')) {
        // --- SSE STREAMING MODE (Ollama) ---
        setStreamingText('');

        const reader = response.body!.getReader();
        const decoder = new TextDecoder();
        let accumulated = '';
        let hasReceivedFirstToken = false;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6).trim();
              if (data === '[DONE]') break;
              try {
                const parsed = JSON.parse(data);
                if (parsed.token) {
                  if (!hasReceivedFirstToken) {
                    hasReceivedFirstToken = true;
                    setIsLoading(false);
                    setIsStreaming(true);
                  }
                  accumulated += parsed.token;
                  setStreamingText(accumulated);
                } else if (parsed.done) {
                  // Stream finished — promote to real message from history
                  await fetchHistory();
                  setStreamingText('');
                }
              } catch (_) { /* ignore parse errors for partial chunks */ }
            }
          }
        }

        setIsStreaming(false);
        setStreamingText('');
        await fetchHistory();
      } else {
        // --- REGULAR JSON MODE (Gemini) ---
        await fetchHistory();
      }
    } catch (error) {
      const networkErrorMsg: Message = {
        id: Date.now() + 1,
        role: 'system',
        content: 'Error: Failed to connect to backend server.',
        created_at: new Date().toISOString()
      };
      setMessages(prev => [...prev, networkErrorMsg]);
    } finally {
      setIsLoading(false);
      setIsStreaming(false);
    }
  };

  const handleResetSession = async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/neo/session/reset`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (response.ok) await fetchHistory();
    } catch (error) {
      console.error('Failed to reset session:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // --- COMMENTED OUT: Human CS escalation handler ---
  // const handleSendEscalation = async () => { ... };

  const handleChipClick = (query: string) => {
    handleSendMessage(query);
  };

  if (!isLoggedIn || isPublicPage || effectivePlan === 'free' || effectivePlan === 'expired') return null;

  return (
    <div 
      ref={widgetRef}
      className="fixed z-[60] font-sans"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        cursor: isDragging ? 'grabbing' : 'grab'
      }}
    >
      {/* Onboarding tooltip */}
      <AnimatePresence>
        {showTooltip && !isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 15, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            className="absolute bottom-full right-0 mb-2 w-64 bg-[#0d0d0d] border border-neutral-800 text-white p-3 rounded-2xl shadow-[0_4px_25px_rgba(0,0,0,0.6)] cursor-pointer select-none"
            onClick={() => { setIsOpen(true); setShowTooltip(false); }}
          >
            <div className="flex items-center gap-2 mb-1">
              <div className="w-5 h-5 rounded-lg bg-red-600/20 border border-red-500/30 flex items-center justify-center text-red-500">
                <Bot size={12} className="animate-pulse" />
              </div>
              <span className="text-[10px] font-black uppercase tracking-wider text-neutral-400">Nera AI Connected</span>
            </div>
            <p className="text-[11px] text-neutral-400 leading-normal font-sans">
              Account issues or need trading analysis? Ask Nera AI now! 📊
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating AI Orb Button with Lottie Animation */}
      <motion.button
        onClick={() => { 
          if (!isDragging) {
            setIsOpen(!isOpen); 
            setShowTooltip(false);
          }
        }}
        onMouseDown={(e) => handleDragStart(e.clientX, e.clientY)}
        onTouchStart={(e) => {
          if (e.touches[0]) {
            handleDragStart(e.touches[0].clientX, e.touches[0].clientY);
          }
        }}
        whileHover={{ scale: isDragging ? 1 : 1.05 }}
        whileTap={{ scale: isDragging ? 1 : 0.95 }}
        style={{
          transform: `rotate(${iconRotation}deg)`,
          transition: isDragging ? 'none' : 'transform 0.15s ease-out'
        }}
        className={`w-14 h-14 rounded-full flex items-center justify-center border outline-none transition-all overflow-visible relative ${
          isOpen
            ? isDarkMode 
              ? 'bg-black/80 backdrop-blur-sm border-neutral-800 text-red-500 shadow-[0_0_20px_rgba(239,68,68,0.3)]'
              : 'bg-white/80 backdrop-blur-sm border-gray-300 text-red-500 shadow-[0_0_20px_rgba(239,68,68,0.2)]'
            : isDarkMode
              ? 'bg-transparent border-neutral-800 text-[#ef4444] shadow-[0_0_25px_rgba(239,68,68,0.15)] hover:shadow-[0_0_30px_rgba(239,68,68,0.25)]'
              : 'bg-transparent border-gray-300 text-[#ef4444] shadow-[0_0_20px_rgba(239,68,68,0.1)] hover:shadow-[0_0_25px_rgba(239,68,68,0.15)]'
        }`}
      >
        {/* Background circle behind Lottie - only when closed */}
        {!isOpen && (
          <div className={`absolute inset-0 rounded-full z-10 transition-all ${
            isDarkMode
              ? 'bg-black/40 backdrop-blur-sm hover:bg-black/60'
              : 'bg-white/40 backdrop-blur-sm hover:bg-white/60'
          }`} />
        )}
        
        {isOpen ? (
          <X size={24} className="relative z-30" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="absolute inset-0 rounded-full animate-ping bg-red-500/20" />
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" className="relative z-10">
              <rect x="5" y="8" width="18" height="14" rx="4" fill="#ef4444" />
              <line x1="14" y1="8" x2="14" y2="4" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
              <circle cx="14" cy="3" r="2" fill="#ef4444"/>
              <rect x="8.5" y="12" width="4" height="4" rx="1.5" fill="white"/>
              <rect x="15.5" y="12" width="4" height="4" rx="1.5" fill="white"/>
              <rect x="9.5" y="13" width="2" height="2" rx="1" fill="#1a1a1a">
                <animate attributeName="x" values="9.5;10.5;9.5" dur="3s" repeatCount="indefinite"/>
              </rect>
              <rect x="16.5" y="13" width="2" height="2" rx="1" fill="#1a1a1a">
                <animate attributeName="x" values="16.5;17.5;16.5" dur="3s" repeatCount="indefinite"/>
              </rect>
              <rect x="10" y="18" width="8" height="2" rx="1" fill="white" opacity="0.8"/>
              <circle cx="23" cy="7" r="1.5" fill="#facc15">
                <animate attributeName="opacity" values="0;1;0" dur="2s" repeatCount="indefinite"/>
                <animate attributeName="r" values="1;2;1" dur="2s" repeatCount="indefinite"/>
              </circle>
            </svg>
          </div>
        )}
      </motion.button>

      {/* Chat Drawer */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 30 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-4 md:absolute md:bottom-full md:right-0 md:mb-4 md:w-[380px] md:inset-auto h-[70vh] md:h-[550px] max-h-[550px] bg-black/90 border border-neutral-900 rounded-3xl shadow-[0_10px_40px_rgba(0,0,0,0.8)] flex flex-col overflow-hidden backdrop-blur-xl"
          >
            {/* Header */}
            <div className="p-4 border-b bg-gradient-to-r from-red-950/20 via-black to-black border-neutral-900 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-red-950/30 border border-red-500/20 flex items-center justify-center text-[#ef4444]">
                  <Bot size={18} />
                </div>
                <div>
                  <h4 className="text-xs font-black uppercase tracking-wider text-white">Nera AI Assistant</h4>
                  <p className="text-[9px] text-neutral-500 font-sans">Expert Trading Coach & Support</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={handleResetSession}
                  disabled={isLoading}
                  title="Reset session"
                  className="p-1.5 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-neutral-400 hover:text-white cursor-pointer transition-colors disabled:opacity-50"
                >
                  <RefreshCw size={12} className={isLoading ? 'animate-spin' : ''} />
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1.5 rounded-lg hover:bg-neutral-900 text-neutral-500 hover:text-white cursor-pointer transition-colors"
                >
                  <X size={14} />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin scrollbar-thumb-neutral-900 scrollbar-track-transparent" data-lenis-prevent>
              {messages.map((msg, i) => {
                const isUser = msg.role === 'user';
                const isSystem = msg.role === 'system';

                if (isSystem) {
                  return (
                    <div key={msg.id || i} className="flex justify-center my-2 select-none">
                      <div className="bg-neutral-950/90 border border-neutral-900 rounded-xl py-1.5 px-3 flex items-center gap-2 max-w-[85%] text-center">
                        <AlertCircle size={10} className="text-amber-500 flex-shrink-0" />
                        <span className="text-[9px] font-sans text-neutral-400 leading-normal">{msg.content}</span>
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={msg.id || i} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                    <div className={`flex gap-2 max-w-[80%] ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] ${isUser
                        ? 'bg-neutral-900 text-neutral-400 border border-neutral-800'
                        : 'bg-red-950/40 text-red-400 border border-red-900/20'
                        }`}>
                        {isUser ? <User size={10} /> : <Bot size={10} />}
                      </div>
                      <div className="flex-1 max-w-full">
                        <div className={`text-[11px] leading-relaxed font-sans ${isUser
                          ? 'p-3 bg-red-600 text-white rounded-2xl rounded-tr-none'
                          : 'p-0 bg-transparent text-neutral-200'
                          }`}>
                          {isUser ? (
                            <div>
                              <p className="mb-0 text-white font-sans">{cleanContent(msg.content)}</p>
                              {msg.attachment_url && msg.attachment_type?.startsWith('image/') && (
                                <div className="mt-2 rounded-xl overflow-hidden max-w-xs border border-white/10 shadow-lg">
                                  <img 
                                    src={msg.attachment_url} 
                                    alt={msg.attachment_name} 
                                    className="w-full max-h-48 object-cover cursor-pointer hover:opacity-90 transition-opacity" 
                                    onClick={() => window.open(msg.attachment_url, '_blank')} 
                                  />
                                </div>
                              )}
                              {msg.attachment_url && !msg.attachment_type?.startsWith('image/') && (
                                <div className="mt-2">
                                  <a
                                    href={msg.attachment_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 p-2 rounded-xl bg-black/40 border border-white/10 hover:border-white/20 hover:bg-black/60 text-[10px] text-white transition-all animate-fade-in"
                                  >
                                    <Paperclip size={10} className="text-white/80" />
                                    <span className="truncate max-w-[150px] font-medium">{msg.attachment_name || 'Attached File'}</span>
                                  </a>
                                </div>
                              )}
                            </div>
                          ) : (
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents} urlTransform={customUrlTransform}>
                              {msg.content}
                            </ReactMarkdown>
                          )}
                        </div>
                        <span className={`text-[8px] text-neutral-600 font-sans block mt-1 ${isUser ? 'text-right' : 'text-left'}`}>
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* --- COMMENTED OUT: Human CS escalation survey bubble --- */}
              {/* {showEscalationSurvey && ( ... )} */}

              {isLoading && (
                <div className="flex justify-start">
                  <div className="flex gap-2 max-w-[80%] items-start">
                    <div className="w-6 h-6 rounded-full bg-red-950/30 text-red-500 border border-red-950/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Bot size={10} className="animate-spin" />
                    </div>
                    <div className="flex-1 max-w-full">
                      <div className="bg-transparent p-0 text-neutral-300 flex flex-col gap-1.5">
                        <span className="text-[10px] text-neutral-400 font-sans leading-normal animate-pulse">
                          {thinkingStatus}
                        </span>
                        <div className="flex gap-1 items-center">
                          <span className="w-1.5 h-1.5 bg-[#ef4444] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-1.5 h-1.5 bg-[#ef4444] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-1.5 h-1.5 bg-[#ef4444] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Streaming bubble — renders token by token for Ollama */}
              {isStreaming && streamingText && (
                <div className="flex justify-start">
                  <div className="flex gap-2 max-w-[80%] flex-row">
                    <div className="w-6 h-6 rounded-full bg-red-950/40 text-red-400 border border-red-900/20 flex items-center justify-center flex-shrink-0">
                      <Bot size={10} />
                    </div>
                    <div className="flex-1 max-w-full">
                      <div className="p-0 text-[11px] leading-relaxed font-sans bg-transparent text-neutral-200">
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents} urlTransform={customUrlTransform}>
                          {streamingText + ' ▋'}
                        </ReactMarkdown>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Quick Chips */}
            {messages.length <= 1 && (
              <div className="p-3 bg-neutral-950/50 border-t border-neutral-950 flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
                <button
                  onClick={() => handleChipClick('How is my trading performance this month?')}
                  className="bg-[#0b0b0b] hover:bg-[#121212] border border-neutral-900 hover:border-neutral-800 text-[10px] text-neutral-400 hover:text-white px-2.5 py-1 rounded-full cursor-pointer transition-all"
                >
                  📊 My Trading Performance
                </button>
                <button
                  onClick={() => handleChipClick('Ask how to sync CEX API on CatatCrypto')}
                  className="bg-[#0b0b0b] hover:bg-[#121212] border border-neutral-900 hover:border-neutral-800 text-[10px] text-neutral-400 hover:text-white px-2.5 py-1 rounded-full cursor-pointer transition-all"
                >
                  💬 Ask How to Use
                </button>
                {/* Discord support chip — replaces old "Hubungi CS Manusia" */}
                <a
                  href="/faq"
                  className="bg-[#0b0b0b] hover:bg-[#121212] border border-neutral-900 hover:border-neutral-800 text-[10px] text-neutral-400 hover:text-white px-2.5 py-1 rounded-full cursor-pointer transition-all flex items-center gap-1"
                >
                  ❓ View FAQ
                </a>
                <a
                  href={DISCORD_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#5865F2]/10 hover:bg-[#5865F2]/20 border border-[#5865F2]/30 hover:border-[#5865F2]/50 text-[10px] text-[#7289da] hover:text-white px-2.5 py-1 rounded-full cursor-pointer transition-all flex items-center gap-1"
                >
                  <MessageCircle size={9} />
                  Discord Support
                  <ExternalLink size={8} className="opacity-60" />
                </a>
              </div>
            )}

            {/* Discord footer bar */}
            <div className="px-4 py-2 bg-[#5865F2]/5 border-t border-[#5865F2]/10 flex items-center justify-between">
                  <span className="text-[9px] text-neutral-600 font-sans">Need more help?</span>
                  <a
                    href={DISCORD_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[9px] font-black uppercase tracking-wider text-[#7289da] hover:text-white transition-colors flex items-center gap-1"
                  >
                    <MessageCircle size={9} />
                    Open Discord Ticket
                    <ExternalLink size={8} />
                  </a>
                </div>

                {/* Selected attachment preview */}
                {(selectedFile || isUploadingFile) && (
                  <div className="px-4 py-2 border-t border-neutral-900 bg-black/95 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-neutral-900 border border-neutral-800 flex items-center justify-center text-neutral-400">
                        {isUploadingFile ? (
                          <RefreshCw size={12} className="animate-spin text-red-500" />
                        ) : selectedFile?.type.startsWith('image/') ? (
                          <Image size={12} className="text-red-500" />
                        ) : (
                          <FileText size={12} className="text-red-500" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] text-white font-medium truncate max-w-[200px]">
                          {selectedFile?.name || 'Uploading...'}
                        </p>
                        <p className="text-[8px] text-neutral-500">
                          {isUploadingFile ? 'Uploading to storage...' : 'File attached'}
                        </p>
                      </div>
                    </div>
                    {!isUploadingFile && (
                      <button
                        onClick={handleClearAttachment}
                        className="p-1 rounded-full hover:bg-neutral-800 text-neutral-400 hover:text-white transition-colors cursor-pointer"
                      >
                        <X size={12} />
                      </button>
                    )}
                  </div>
                )}

                {/* Message input */}
                <div className="p-4 border-t border-neutral-900 bg-black flex gap-2 items-center">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="image/*,application/pdf,.csv,.xlsx,.xls"
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isLoading || isUploadingFile}
                    className="w-9 h-9 rounded-xl bg-[#0b0b0b] border border-neutral-900 hover:border-neutral-800 text-neutral-400 hover:text-white flex items-center justify-center cursor-pointer transition-all active:scale-95 disabled:opacity-50"
                  >
                    <Paperclip size={14} />
                  </button>
                  <input
                    type="text"
                    value={inputMessage}
                    onChange={e => setInputMessage(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleSendMessage(inputMessage); }}
                    onPaste={handlePaste}
                    disabled={isLoading || isUploadingFile}
                    placeholder={isUploadingFile ? "Processing file..." : "Ask Nera AI..."}
                    className="flex-1 bg-[#0b0b0b] border border-neutral-900 hover:border-neutral-800 focus:border-red-600/50 focus:outline-none rounded-xl py-2 px-3.5 text-xs text-white placeholder-neutral-700 disabled:opacity-50 font-sans"
                  />
                  <button
                    onClick={() => handleSendMessage(inputMessage)}
                    disabled={isLoading || isUploadingFile || (!inputMessage.trim() && !attachedUrl)}
                    className="w-9 h-9 rounded-xl bg-red-600 hover:bg-red-700 text-white flex items-center justify-center cursor-pointer transition-all active:scale-95 disabled:opacity-50"
                  >
                    <Send size={14} />
                  </button>
                </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NeoAIWidget;
